/**
 * AI Integration Hooks
 * Custom React hooks following existing project patterns
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { 
  AIError, 
  AIMessage, 
  AIConversation, 
  StructuredContentRequest,
  StructuredContentResponse,
  ContentAnalysisResponse,
  AIModelType,
  SupportedLanguage,
  MessageType,
  MessageStatus,
  ConversationStatus,
  UseAIState,
  UseChatbotState,
  UseStructuredContentState
} from '../types/aiTypes';
import aiApiService from '../services/aiApiService';
import { AI_FEATURES, getEnabledLanguages } from '../config/aiConfig';

// ================== BASE AI HOOK ==================

export const useAI = () => {
  const [state, setState] = useState<UseAIState>({
    isLoading: false,
    error: null,
    requestId: null
  });

  const makeRequest = useCallback(async <T>(
    operation: () => Promise<T>,
    errorContext?: string
  ): Promise<T> => {
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      requestId,
      lastRequest: {
        timestamp: new Date(),
        endpoint: errorContext || 'unknown',
        payload: null
      }
    }));

    try {
      const result = await operation();
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: null
      }));
      
      return result;
    } catch (error) {
      const aiError = error instanceof AIError ? error : new AIError(
        error instanceof Error ? error.message : 'Unknown error occurred',
        'UNKNOWN_ERROR' as any,
        false,
        { originalError: error, context: errorContext }
      );
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: aiError
      }));
      
      throw aiError;
    }
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const clearState = useCallback(() => {
    setState({
      isLoading: false,
      error: null,
      requestId: null
    });
  }, []);

  return {
    ...state,
    makeRequest,
    clearError,
    clearState
  };
};

// ================== CHATBOT HOOK ==================

export const useChatbot = (options?: {
  defaultLanguage?: SupportedLanguage;
  maxConversations?: number;
  autoSave?: boolean;
}) => {
  const { makeRequest, isLoading, error } = useAI();
  const [state, setState] = useState<Omit<UseChatbotState, 'isLoading' | 'error'>>({
    conversations: {},
    currentConversationId: null,
    connectionStatus: 'connected'
  });

  const conversationsRef = useRef(state.conversations);
  conversationsRef.current = state.conversations;

  // Generate unique conversation ID
  const generateConversationId = useCallback(() => {
    return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Generate unique message ID  
  const generateMessageId = useCallback(() => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Create new conversation
  const createConversation = useCallback((
    language: SupportedLanguage = options?.defaultLanguage || 'English',
    topic?: string
  ): string => {
    const conversationId = generateConversationId();
    
    const conversation: AIConversation = {
      id: conversationId,
      title: topic || 'New Conversation',
      topic,
      language,
      status: ConversationStatus.ACTIVE,
      messages: [],
      createdAt: new Date(),
      lastActivity: new Date(),
      metadata: {
        model: 'GPT_4' as any,
        totalMessages: 0,
        totalTokens: 0,
        averageResponseTime: 0
      }
    };

    setState(prev => ({
      ...prev,
      conversations: {
        ...prev.conversations,
        [conversationId]: conversation
      },
      currentConversationId: conversationId
    }));

    return conversationId;
  }, [generateConversationId, options?.defaultLanguage]);

  // Add message to conversation
  const addMessage = useCallback((
    conversationId: string,
    content: string,
    type: MessageType,
    status: MessageStatus = MessageStatus.SENT
  ): string => {
    const messageId = generateMessageId();
    
    const message: AIMessage = {
      id: messageId,
      conversationId,
      type,
      content,
      timestamp: new Date(),
      status,
      metadata: {
        language: conversationsRef.current[conversationId]?.language || 'English'
      }
    };

    setState(prev => {
      const conversation = prev.conversations[conversationId];
      if (!conversation) return prev;

      const updatedConversation: AIConversation = {
        ...conversation,
        messages: [...conversation.messages, message],
        lastActivity: new Date(),
        metadata: {
          model: conversation.metadata?.model || AIModelType.GPT_4,
          totalMessages: (conversation.metadata?.totalMessages || 0) + 1,
          totalTokens: conversation.metadata?.totalTokens || 0,
          averageResponseTime: conversation.metadata?.averageResponseTime || 0,
          userSatisfaction: conversation.metadata?.userSatisfaction,
          tags: conversation.metadata?.tags
        }
      };

      return {
        ...prev,
        conversations: {
          ...prev.conversations,
          [conversationId]: updatedConversation
        }
      };
    });

    return messageId;
  }, [generateMessageId]);

  // Update message status
  const updateMessageStatus = useCallback((
    conversationId: string,
    messageId: string,
    status: MessageStatus,
    error?: { code: string; message: string; retryable: boolean }
  ) => {
    setState(prev => {
      const conversation = prev.conversations[conversationId];
      if (!conversation) return prev;

      const updatedMessages = conversation.messages.map(msg => 
        msg.id === messageId 
          ? { ...msg, status, ...(error && { error }) }
          : msg
      );

      return {
        ...prev,
        conversations: {
          ...prev.conversations,
          [conversationId]: {
            ...conversation,
            messages: updatedMessages
          }
        }
      };
    });
  }, []);

  // Send message
  const sendMessage = useCallback(async (
    message: string,
    conversationId?: string,
    topic?: string,
    language: SupportedLanguage = options?.defaultLanguage || 'English'
  ): Promise<{ conversationId: string; messageId: string; response: string }> => {
    if (!AI_FEATURES.CHATBOT_ENABLED) {
      throw new AIError('Chatbot feature is disabled', 'FEATURE_DISABLED' as any);
    }

    // Create conversation if none provided
    let activeConversationId = conversationId;
    if (!activeConversationId) {
      activeConversationId = createConversation(language, topic);
    }

    // Add user message
    const userMessageId = addMessage(
      activeConversationId,
      message,
      MessageType.USER,
      MessageStatus.SENDING
    );

    try {
      // Send to AI service
      const response = await makeRequest(
        () => aiApiService.sendChatMessage(activeConversationId!, message, topic, language),
        'sendChatMessage'
      );

      // Update user message status
      updateMessageStatus(activeConversationId, userMessageId, MessageStatus.SENT);

      // Add AI response
      const aiMessageId = addMessage(
        activeConversationId,
        response,
        MessageType.AI,
        MessageStatus.SENT
      );

      return {
        conversationId: activeConversationId,
        messageId: aiMessageId,
        response
      };

    } catch (error) {
      // Update user message with error status
      updateMessageStatus(
        activeConversationId,
        userMessageId,
        MessageStatus.ERROR,
        {
          code: error instanceof AIError ? error.code : 'UNKNOWN_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
          retryable: error instanceof AIError ? error.retryable : false
        }
      );
      
      throw error;
    }
  }, [makeRequest, createConversation, addMessage, updateMessageStatus, options?.defaultLanguage]);

  // Clear conversation
  const clearConversation = useCallback(async (conversationId: string) => {
    try {
      await makeRequest(
        () => aiApiService.clearConversation(conversationId),
        'clearConversation'
      );

      setState(prev => {
        const newConversations = { ...prev.conversations };
        delete newConversations[conversationId];

        return {
          ...prev,
          conversations: newConversations,
          currentConversationId: prev.currentConversationId === conversationId 
            ? null 
            : prev.currentConversationId
        };
      });
    } catch (error) {
      console.error('Failed to clear conversation:', error);
      throw error;
    }
  }, [makeRequest]);

  // Switch conversation
  const switchConversation = useCallback((conversationId: string | null) => {
    setState(prev => ({
      ...prev,
      currentConversationId: conversationId
    }));
  }, []);

  // Get conversation
  const getConversation = useCallback((conversationId: string): AIConversation | null => {
    return state.conversations[conversationId] || null;
  }, [state.conversations]);

  // Get current conversation
  const getCurrentConversation = useCallback((): AIConversation | null => {
    return state.currentConversationId 
      ? state.conversations[state.currentConversationId] 
      : null;
  }, [state.conversations, state.currentConversationId]);

  // Start new conversation
  const startNewConversation = useCallback((
    language?: SupportedLanguage,
    topic?: string
  ): string => {
    return createConversation(language, topic);
  }, [createConversation]);

  return {
    // State
    conversations: state.conversations,
    currentConversationId: state.currentConversationId,
    connectionStatus: state.connectionStatus,
    isLoading,
    error,
    
    // Actions
    sendMessage,
    clearConversation,
    switchConversation,
    startNewConversation,
    
    // Getters
    getConversation,
    getCurrentConversation,
    
    // Utilities
    generateConversationId,
    enabledLanguages: getEnabledLanguages()
  };
};

// ================== STRUCTURED CONTENT HOOK ==================

export const useStructuredContent = () => {
  const { makeRequest, isLoading, error } = useAI();
  const [state, setState] = useState<Omit<UseStructuredContentState, 'isLoading' | 'error'>>({
    structuredContent: null,
    generationHistory: [],
    isGenerating: false
  });

  // Utility function to validate and normalize structured content response
  const validateStructuredContent = (content: StructuredContentResponse): StructuredContentResponse => {
    const normalized: StructuredContentResponse = {
      id: content.id || Date.now().toString(),
      title: content.title || 'Untitled',
      description: content.description || '',
      body: content.body || '',
      language: content.language || 'English',
      category: content.category || 'General',
      tags: content.tags || [],
      summary: content.summary || '',
      metadata: {
        wordCount: content.metadata?.wordCount || 0,
        readingTime: content.metadata?.readingTime || 0,
        seoScore: content.metadata?.seoScore,
        sentiment: content.metadata?.sentiment || 'neutral',
        topics: content.metadata?.topics || [],
        generatedAt: content.metadata?.generatedAt || new Date(),
        model: content.metadata?.model || AIModelType.GPT_4,
        processingTime: content.metadata?.processingTime || 0
      },
      suggestions: content.suggestions
    };
    return normalized;
  };

  // Generate structured content
  const generateStructuredContent = useCallback(async (
    request: StructuredContentRequest
  ): Promise<StructuredContentResponse> => {
    if (!AI_FEATURES.STRUCTURED_CONTENT_ENABLED) {
      throw new AIError('Structured content generation is disabled', 'FEATURE_DISABLED' as any);
    }

    const rawResult = await makeRequest(
      () => aiApiService.generateStructuredContent(request),
      'generateStructuredContent'
    );

    const result = validateStructuredContent(rawResult);

    setState(prev => ({
      ...prev,
      structuredContent: result,
      generationHistory: [result, ...prev.generationHistory.slice(0, 9)] // Keep last 10
    }));

    return result;
  }, [makeRequest]);

  // Quick generate
  const quickGenerate = useCallback(async (
    inputContent: string,
    language: SupportedLanguage
  ): Promise<StructuredContentResponse> => {
    if (!AI_FEATURES.STRUCTURED_CONTENT_ENABLED) {
      throw new AIError('Structured content generation is disabled', 'FEATURE_DISABLED' as any);
    }

    const rawResult = await makeRequest(
      () => aiApiService.generateQuickStructuredContent(inputContent, language),
      'quickGenerateStructuredContent'
    );

    const result = validateStructuredContent(rawResult);

    setState(prev => ({
      ...prev,
      structuredContent: result,
      generationHistory: [result, ...prev.generationHistory.slice(0, 9)]
    }));

    return result;
  }, [makeRequest]);

  // Clear content
  const clearContent = useCallback(() => {
    setState(prev => ({
      ...prev,
      structuredContent: null
    }));
  }, []);

  // Clear history
  const clearHistory = useCallback(() => {
    setState(prev => ({
      ...prev,
      generationHistory: []
    }));
  }, []);

  // Select from history
  const selectFromHistory = useCallback((content: StructuredContentResponse) => {
    setState(prev => ({
      ...prev,
      structuredContent: content
    }));
  }, []);

  return {
    // State
    structuredContent: state.structuredContent,
    generationHistory: state.generationHistory,
    isLoading,
    error,
    
    // Actions
    generateStructuredContent,
    quickGenerate,
    clearContent,
    clearHistory,
    selectFromHistory,
    
    // Utilities
    enabledLanguages: getEnabledLanguages()
  };
};

// ================== CONTENT ANALYSIS HOOK ==================

export const useContentAnalysis = () => {
  const { makeRequest, isLoading, error } = useAI();
  const [analysisResults, setAnalysisResults] = useState<Record<string, ContentAnalysisResponse>>({});

  // Summarize content
  const summarizeContent = useCallback(async (
    content: string,
    language: SupportedLanguage = 'English',
    maxLength?: number
  ): Promise<ContentAnalysisResponse> => {
    if (!AI_FEATURES.CONTENT_ANALYSIS_ENABLED) {
      throw new AIError('Content analysis is disabled', 'FEATURE_DISABLED' as any);
    }

    const result = await makeRequest(
      () => aiApiService.summarizeContent(content, language, maxLength),
      'summarizeContent'
    );

    const key = `summary_${Date.now()}`;
    setAnalysisResults(prev => ({ ...prev, [key]: result }));

    return result;
  }, [makeRequest]);

  // Fact check content
  const factCheckContent = useCallback(async (
    content: string,
    language: SupportedLanguage = 'English'
  ): Promise<ContentAnalysisResponse> => {
    if (!AI_FEATURES.CONTENT_ANALYSIS_ENABLED) {
      throw new AIError('Content analysis is disabled', 'FEATURE_DISABLED' as any);
    }

    const result = await makeRequest(
      () => aiApiService.factCheckContent(content, language),
      'factCheckContent'
    );

    const key = `factcheck_${Date.now()}`;
    setAnalysisResults(prev => ({ ...prev, [key]: result }));

    return result;
  }, [makeRequest]);

  // Generate headlines
  const generateHeadlines = useCallback(async (
    content: string,
    language: SupportedLanguage = 'English',
    count: number = 5
  ): Promise<string[]> => {
    if (!AI_FEATURES.CONTENT_ANALYSIS_ENABLED) {
      throw new AIError('Content analysis is disabled', 'FEATURE_DISABLED' as any);
    }

    return makeRequest(
      () => aiApiService.generateHeadlines(content, language, count),
      'generateHeadlines'
    );
  }, [makeRequest]);

  // Clear analysis results
  const clearResults = useCallback(() => {
    setAnalysisResults({});
  }, []);

  return {
    // State
    analysisResults,
    isLoading,
    error,
    
    // Actions
    summarizeContent,
    factCheckContent,
    generateHeadlines,
    clearResults,
    
    // Utilities
    enabledLanguages: getEnabledLanguages()
  };
};

// ================== HEALTH MONITORING HOOK ==================

export const useAIHealth = () => {
  const [healthStatus, setHealthStatus] = useState<{
    status: 'healthy' | 'degraded' | 'unhealthy';
    lastCheck: Date | null;
    services: Record<string, boolean>;
  }>({
    status: 'healthy',
    lastCheck: null,
    services: {}
  });

  const [isChecking, setIsChecking] = useState(false);

  const checkHealth = useCallback(async () => {
    setIsChecking(true);
    
    try {
      const health = await aiApiService.healthCheck();
      setHealthStatus({
        status: health.status,
        lastCheck: new Date(),
        services: health.services
      });
    } catch (error) {
      setHealthStatus({
        status: 'unhealthy',
        lastCheck: new Date(),
        services: {}
      });
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Auto health check on mount
  useEffect(() => {
    checkHealth();
    
    // Set up periodic health checks
    const interval = setInterval(checkHealth, 5 * 60 * 1000); // Every 5 minutes
    
    return () => clearInterval(interval);
  }, [checkHealth]);

  return {
    healthStatus,
    isChecking,
    checkHealth
  };
};
